import { useCallback, useMemo, useState } from 'react'

import type { AdditionalCostType } from '@api'
import type { CellContext, ColumnDef } from '@tanstack/react-table'

import IconEdit from '@/assets/icons/icon-edit.svg'
import IconTrash from '@/assets/icons/icon-trash.svg'
import { Button } from '@/components/ui/button.tsx'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { useAccountingAdditionCostTypeMutations } from '@/modules/accounting/hooks/useAccountingAdditionCostTypeMutations.ts'
import { useAccountingAdditionalCostTypes } from '@/modules/accounting/hooks/useAccountingAdditionalCostTypes.ts'
import { AccountingAdditionalCostTypeEditableCell } from '@/modules/accounting/ui/AdditionalCostTypes/AccountingAdditionalCostTypeEditableCell.tsx'
import { AccountingAdditionalCostTypesTableSkeleton } from '@/modules/accounting/ui/AdditionalCostTypes/AccountingAdditionalCostTypesTableSkeleton.tsx'
import { IconButton } from '@/modules/chatting/ui/components/icon-button.tsx'

export const AccountingAdditionalCostTypesTable = () => {
  const [editingType, setEditingType] = useState<AdditionalCostType>()
  const { updateCostType } = useAccountingAdditionCostTypeMutations()

  const { isLoading: isCostTypesLoading, data: costTypes } =
    useAccountingAdditionalCostTypes()
  const { deleteCostType } = useAccountingAdditionCostTypeMutations()

  const handleEditValueSubmit = useCallback(
    async (value: string) => {
      if (editingType) {
        try {
          setEditingType(undefined)
          if (editingType.id) {
            await updateCostType.mutateAsync({
              id: editingType.id,
              name: value,
            })
          }
        } catch (error) {
          console.error(error)
        }
      }
    },
    [editingType, updateCostType],
  )

  const handleSelectCostType = useCallback(
    (item: AdditionalCostType) => {
      if (editingType?.id === item.id) {
        setEditingType(undefined)

        return
      }
      setEditingType(item)
    },
    [editingType?.id],
  )

  const columns: ColumnDef<AdditionalCostType>[] = useMemo(() => {
    return [
      {
        accessorKey: 'name',
        header: 'Name',
        sortingFn: 'auto',
        cell: (cellContext: CellContext<AdditionalCostType, string>) =>
          editingType?.id === cellContext.row.original.id ? (
            <AccountingAdditionalCostTypeEditableCell
              onSubmit={handleEditValueSubmit}
              onBlur={() => setEditingType(undefined)}
              {...cellContext}
            />
          ) : (
            <span>{cellContext.row.original.name}</span>
          ),
      },
      {
        id: 'actions',
        header: <div className="w-full justify-end text-right">Action</div>,
        cell: function ActionsCell({ row }) {
          return (
            <div className="flex justify-end gap-2">
              <IconButton
                icon={IconTrash}
                className="hover:text-danger-hover text-danger-default"
                onClick={() =>
                  openDialog({
                    type: DIALOG_TYPE.ACCOUNTING_ADDITIONAL_COST_TYPE_DELETE,
                    data: row.original,
                    onSubmit: deleteCostType.mutateAsync,
                  })
                }
              />
              <IconButton
                icon={IconEdit}
                className="hover:text-primary-hover text-primary-default"
                onClick={() => handleSelectCostType(row.original)}
              />
            </div>
          )
        },
      },
    ] as ColumnDef<AdditionalCostType>[]
  }, [
    editingType?.id,
    handleEditValueSubmit,
    deleteCostType.mutateAsync,
    handleSelectCostType,
  ])

  const handleOpenCreateDialog = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.ACCOUNTING_ADDITIONAL_COST_TYPE_CREATE,
    })
  }, [])

  return isCostTypesLoading ? (
    <AccountingAdditionalCostTypesTableSkeleton columns={columns} />
  ) : (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end pt-5">
        <Button onClick={handleOpenCreateDialog}>+ Create new</Button>
      </div>
      <div className="max-h-[50vh] overflow-y-auto">
        <DataTable
          hasActionColumn
          columns={columns}
          enablePagination
          data={costTypes}
        />
      </div>
    </div>
  )
}
